"use client";

import { Box, Typography } from "@mui/material";
import { useEffect } from "react";
import ButtonToStart from "@/src/components/common/button-to-start/button-to-start";
import ActionButton from "@/src/components/common/broadsheet/action-button";
import ErrorMark from "@/src/components/common/broadsheet/error-mark";
import Kicker from "@/src/components/common/broadsheet/kicker";
import {
  actionSx,
  doubleRule,
  FONT_DISPLAY,
  FONT_TEXT,
  HEADING_SIZE,
  SHEET_GUTTER,
  SHEET_MAX_WIDTH,
} from "@/src/components/common/broadsheet/tokens";

function isServedFromOwnHost() {
  try {
    return (
      window.location.host === new URL(process.env.NEXT_PUBLIC_SITE_URL!).host
    );
  } catch {
    return true;
  }
}

export default function Error({
  error,
}: {
  error: Error & { digest?: string };
}) {
  useEffect(() => {
    if (
      process.env.NEXT_PUBLIC_REPORT_ERROR === "true" &&
      isServedFromOwnHost() &&
      !error.message.includes("Failed to load chunk") &&
      !error.message.includes("Loading chunk") &&
      !error.message.includes("Connection closed.") &&
      !error.message.includes("network error")
    ) {
      const data = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: error.message,
          digest: error.digest || null,
          stack: error.stack || null,
          cause: error.cause || null,
          location: window?.location?.href,
        }),
      };

      fetch("/api/errors/report/telegram", data);
      fetch("/api/errors/report/redmine", data);
    }
  }, [error]);

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "custom.newsprint",
        }}
      >
        <Box
          component="main"
          sx={{
            maxWidth: SHEET_MAX_WIDTH,
            mx: "auto",
            px: SHEET_GUTTER,
            pt: { xs: "6vh", lg: "12vh" },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 22rem" },
              alignItems: "center",
              columnGap: 2.5,
              rowGap: 1.5,
              pb: { xs: 2, md: 3 },
              ...doubleRule,
              "& > *": { minWidth: 0 },
            }}
          >
            <Box>
              <Kicker sx={{ mb: 0.375 }}>Something went wrong</Kicker>
              <Typography
                component="h1"
                sx={{
                  m: 0,
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 700,
                   fontSize: HEADING_SIZE.page,
                  lineHeight: 1.05,
                  letterSpacing: "0.01em",
                  color: "custom.ink",
                }}
              >
                This edition failed to print
              </Typography>
              <Typography
                component="p"
                sx={{
                  m: 0,
                  mt: 0.5,
                  fontFamily: FONT_TEXT,
                  fontSize: { xs: "0.9375rem", md: "1rem" },
                  lineHeight: 1.6,
                  color: "custom.inkMuted",
                  maxWidth: "52ch",
                }}
              >
                An unexpected error stopped this page from loading. The problem
                has been reported; trying again often clears it.
              </Typography>
              <Box
                sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1.25 }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => window.location.reload()}
                  sx={{ ...actionSx("solid"), cursor: "pointer" }}
                >
                  Try again
                </Box>
                <ActionButton href="/" variant="outline">
                  Return home
                </ActionButton>
              </Box>
              {error.digest ? (
                <Typography
                  component="p"
                  sx={{
                    m: 0,
                    mt: 1,
                    fontFamily: FONT_TEXT,
                    fontSize: "0.71875rem",
                    color: "custom.subtitleText",
                  }}
                >
                  Reference {error.digest}
                </Typography>
              ) : null}
            </Box>
            <ErrorMark code="500" label="Error" />
          </Box>
        </Box>
      </Box>
      <ButtonToStart />
    </>
  );
}
